// York drop-off — "take it to the drop zone", reached two ways:
//
//   'full'    the picker tapped 4/4 on the fill prompt.
//   'no_fit'  they reported a part-full york and the server came back
//             YORK_FULL: orders ARE queued, none of them fit the space
//             left. Measured at roughly a quarter of all drops, nearly
//             all of them from 3/4.
//
// The reason line is the only difference, and it is the point of the
// variant: a picker who taps 3/4 and lands on DROP OFF YORK with no
// explanation reads it as a mis-tap.
//
// The trip is measured, not just counted: a york_drops row opens as
// this screen appears (before the walk), the confirm stamps the moment
// they were AT the zone, and their next wave start stamps the resume.
// Confirming hands back to the scan-location prompt with an empty york,
// which is what makes the next order allocate against a clear one.
import { useEffect, useRef, useState } from "react";
import { MapPin, Check, Info } from "lucide-react";
import YorkGlyph from "../picking-shared/YorkGlyph";
import type { YorkQuarters } from "./YorkFillPrompt";
import { useTranslation } from "../../i18n";
import "./YorkDropOff.css";

export type YorkDropReason = "full" | "no_fit";

interface Props {
    reason: YorkDropReason;
    /** What the picker reported, so the glyph shows their own answer. */
    quarters: YorkQuarters;
    /** Fired once as the screen appears, so the measurement's clock
     *  starts before the walk rather than after it. */
    onShown?: () => void | Promise<void>;
    /** Confirm — the caller closes the measurement and moves on. */
    onConfirm: () => void | Promise<void>;
}

const YorkDropOff = ({ reason, quarters, onShown, onConfirm }: Props) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    // Once per mount. StrictMode double-invokes effects in development,
    // and a second call would open a second york_drops row and orphan
    // the first with no confirm and no resume.
    const shownRef = useRef(false);
    useEffect(() => {
        if (shownRef.current) return;
        shownRef.current = true;
        void onShown?.();
        // Deliberately mount-only: onShown is an inline closure, so
        // depending on it would re-fire on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirm = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="ydo">
            {reason === "no_fit" && (
                <div className="ydo-reason">
                    <Info size={17} strokeWidth={2.4} aria-hidden="true" />
                    {t("picking_york_nothing_fits")}
                </div>
            )}

            <YorkGlyph
                quarters={quarters}
                size={reason === "no_fit" ? 112 : 130}
                tone="#b84700"
                className="ydo-glyph"
            />

            <div className="ydo-command">
                {t("picking_york_drop_off")
                    .toUpperCase()
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((word, i) => (
                        <span key={i} className="ydo-command-word">{word}</span>
                    ))}
            </div>

            <div className="ydo-card" role="status">
                <div className="ydo-card-main">
                    <MapPin size={26} strokeWidth={1.7} className="ydo-card-icon" aria-hidden="true" />
                    <span className="ydo-card-title">{t("picking_york_drop_zone")}</span>
                </div>
                <span className="ydo-card-sub">{t("picking_york_bring_empty")}</span>
            </div>

            <div className="ydo-spacer" />

            <button
                type="button"
                className="ydo-confirm"
                disabled={busy}
                onClick={() => { void confirm(); }}
            >
                <Check size={20} strokeWidth={2.4} aria-hidden="true" />
                {t("picking_york_dropped")}
            </button>
        </div>
    );
};

export default YorkDropOff;
